import {
  DEFAULT_NAMESPACE,
  Entity,
  RELATION_MEMBER_OF,
  RELATION_PARENT_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { InfoCard, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef, getEntityRelations, useEntity } from '@backstage/plugin-catalog-react';
import {
  Accordion,
  AccordionGroup,
  AccordionPanel,
  AccordionTrigger,
  Avatar,
  Box,
  Card,
  Flex,
  Link,
  Text,
} from '@backstage/ui';
import { makeStyles } from '@material-ui/core/styles';
import { useState } from 'react';
import useAsync from 'react-use/esm/useAsync';
import {
  RELATION_DIRECTOR_OF,
  RELATION_ENGINEER_MANAGER_OF,
  RELATION_PRINCIPAL_ENGINEER_OF,
  RELATION_TECH_LEAD_OF,
} from '@developer-portal/plugin-catalog-common';

/**
 * Role definitions in hierarchy order.
 * To add a new role, add an entry here — no other changes required.
 */
const ROLE_DEFINITIONS = [
  { relationType: RELATION_ENGINEER_MANAGER_OF, title: 'Engineering Managers' },
  { relationType: RELATION_TECH_LEAD_OF, title: 'Tech Leads' },
  { relationType: RELATION_DIRECTOR_OF, title: 'Directors' },
  { relationType: RELATION_PRINCIPAL_ENGINEER_OF, title: 'Principal Engineers' },
  { relationType: RELATION_MEMBER_OF, title: 'Members' },
] as const;

async function getDescendantGroupMembers(
  groupEntity: Entity,
  catalogApi: CatalogApi,
  relationType: string,
): Promise<Entity[]> {
  const groupNamespace = (groupEntity.metadata.namespace ?? DEFAULT_NAMESPACE).toLocaleLowerCase('en-US');
  const groupName = groupEntity.metadata.name.toLocaleLowerCase('en-US');

  const visited = new Map<string, boolean>();
  const queue: Array<{ kind: string; namespace: string; name: string }> = [
    { kind: 'group', namespace: groupNamespace, name: groupName },
  ];
  visited.set(stringifyEntityRef({ kind: 'Group', namespace: groupNamespace, name: groupName }), true);

  const descendantRefs: Array<{ kind: string; namespace: string; name: string }> = [];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentEntity = await catalogApi.getEntityByRef(current);
    if (!currentEntity) continue;

    const children = getEntityRelations(currentEntity, RELATION_PARENT_OF, { kind: 'Group' });
    for (const child of children) {
      const childRef = stringifyEntityRef(child);
      if (!visited.has(childRef)) {
        visited.set(childRef, true);
        queue.push(child);
        descendantRefs.push(child);
      }
    }
  }

  if (descendantRefs.length === 0) return [];

  const result = await catalogApi.getEntities({
    filter: {
      kind: 'User',
      [`relations.${relationType.toLocaleLowerCase('en-US')}`]: descendantRefs.map(ref =>
        stringifyEntityRef({ kind: 'group', namespace: ref.namespace, name: ref.name }),
      ),
    },
  });
  return result.items;
}

function dedupeEntities(entities: Entity[]): Entity[] {
  const seen = new Map<string, boolean>();
  return entities.filter(e => {
    const ref = stringifyEntityRef(e);
    if (seen.has(ref)) return false;
    seen.set(ref, true);
    return true;
  });
}

const useMemberStyles = makeStyles({
  card: {
    display: 'flex',
    gap: 'var(--bui-space-3)',
    padding: 'var(--bui-space-3)',
    alignItems: 'flex-start',
    flexDirection: 'row',
    height: 140,
    overflow: 'hidden',
  },
  avatar: {
    flexShrink: 0,
  },
  cardTextContainer: {
    overflow: 'hidden',
  },
  singlelineEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  memberList: {
    display: 'grid',
    gap: 'var(--bui-space-3)',
    gridTemplateColumns: 'repeat(auto-fit, minmax(275px, 1fr))',
    gridAutoRows: '1fr',
    margin: 0,
    padding: 0,
    paddingTop: 'var(--bui-space-3)',
    listStyle: 'none',
  },
  memberListItem: {
    display: 'contents',
  },
});

function MemberCard({ member }: { readonly member: Entity }) {
  const classes = useMemberStyles();
  const {
    metadata: { name: metaName, description: metaDescription },
    spec,
  } = member;
  const profile = (spec as any)?.profile ?? {};
  const displayName: string = profile.displayName ?? metaName;
  const jobTitle: string | undefined = metaDescription;

  return (
    <Card className={classes.card}>
      <Avatar
        className={classes.avatar}
        name={displayName}
        src={profile.picture ?? ''}
        purpose="decoration"
        size="x-large"
      />
      <Flex className={classes.cardTextContainer} direction="column" gap="1">
        <Text variant="body-large" as="h4">
          {displayName}
        </Text>
        {profile.email && (
          <Link className={classes.singlelineEllipsis} href={`mailto:${profile.email}`}>
            {profile.email}
          </Link>
        )}
        {jobTitle && (
          <Text variant="body-medium" className={classes.singlelineEllipsis}>
            {jobTitle}
          </Text>
        )}
      </Flex>
    </Card>
  );
}

function RoleSection({
  relationType,
  title,
  groupName,
  groupNamespace,
  showAggregateMembers,
  groupEntity,
}: {
  readonly relationType: string;
  readonly title: string;
  readonly groupName: string;
  readonly groupNamespace: string;
  readonly showAggregateMembers: boolean;
  readonly groupEntity: Entity;
}) {
  const classes = useMemberStyles();
  const catalogApi = useApi(catalogApiRef);

  const {
    loading: loadingDirect,
    error,
    value: directMembers,
  } = useAsync(async () => {
    const result = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        [`relations.${relationType.toLocaleLowerCase('en-US')}`]: [
          stringifyEntityRef({
            kind: 'group',
            namespace: groupNamespace.toLocaleLowerCase('en-US'),
            name: groupName.toLocaleLowerCase('en-US'),
          }),
        ],
      },
    });
    return result.items;
  }, [catalogApi, groupEntity, relationType]);

  const { loading: loadingDescendant, value: descendantMembers } = useAsync(async () => {
    if (!showAggregateMembers) return [];
    return await getDescendantGroupMembers(groupEntity, catalogApi, relationType);
  }, [catalogApi, groupEntity, showAggregateMembers, relationType]);

  const loading = loadingDirect || (showAggregateMembers && loadingDescendant);

  const members = dedupeEntities(
    [...(directMembers ?? []), ...(showAggregateMembers && descendantMembers ? descendantMembers : [])].sort((a, b) =>
      stringifyEntityRef(a).localeCompare(stringifyEntityRef(b)),
    ),
  );

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  // Hide this accordion section when there are no members
  if (!loading && members.length === 0) {
    return null;
  }

  return (
    <Accordion>
      <AccordionTrigger>
        {title}
        {loading ? '' : ` (${members.length})`}
      </AccordionTrigger>
      <AccordionPanel>
        {loading ? (
          <Progress />
        ) : (
          <ul className={classes.memberList}>
            {members.map(member => (
              <li key={stringifyEntityRef(member)} className={classes.memberListItem}>
                <MemberCard member={member} />
              </li>
            ))}
          </ul>
        )}
      </AccordionPanel>
    </Accordion>
  );
}

/**
 * Displays all team roles in a single accordion card, in hierarchy order:
 * Engineering Managers → Tech Leads → Directors → Principal Engineers.
 *
 * Sections with no members are automatically hidden.
 * The "Include sub-groups" toggle aggregates members from child groups.
 *
 * To add a new role, add an entry to the ROLE_DEFINITIONS array at the top of the file.
 */
export function GroupRolesCard() {
  const { entity: groupEntity } = useEntity();
  const [showAggregateMembers, setShowAggregateMembers] = useState(false);

  const {
    metadata: { name: groupName, namespace: grpNamespace },
  } = groupEntity;
  const groupNamespace = grpNamespace || DEFAULT_NAMESPACE;

  return (
    <InfoCard
      title={`${groupName} members`}
      action={
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16 }}>
          <Text variant="body-medium" as="label" style={{ cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={showAggregateMembers}
              onChange={e => setShowAggregateMembers(e.target.checked)}
              style={{ marginRight: 6 }}
            />{' '}
            Include sub-groups
          </Text>
        </Box>
      }
    >
      <AccordionGroup allowsMultiple>
        {ROLE_DEFINITIONS.map(({ relationType, title }) => (
          <RoleSection
            key={relationType}
            relationType={relationType}
            title={title}
            groupName={groupName}
            groupNamespace={groupNamespace}
            showAggregateMembers={showAggregateMembers}
            groupEntity={groupEntity}
          />
        ))}
      </AccordionGroup>
    </InfoCard>
  );
}
